import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useWebSocket } from "./use-websocket";

type TicketEvent = {
  ticket_id?: string;
  message_id?: string;
  organization_id?: string;
  mailbox_id?: string;
};

function asEvent(payload: unknown): TicketEvent {
  return (payload ?? {}) as TicketEvent;
}

/**
 * Wire server-pushed events into TanStack Query cache invalidation.
 *
 * `ticket.created`            → invalidate the inbox list.
 * `ticket.message_added`      → invalidate list + the specific ticket detail (inbound).
 * `ticket.message_sent`       → same (outbound send succeeded).
 * `ticket.message_failed`     → invalidate detail so the row flips to "failed".
 * `ticket.status_changed`     → invalidate list + detail so the status chip updates.
 * `ticket.note_added`         → invalidate detail so the new note shows up.
 * `ticket.note_deleted`       → same.
 * `ticket.assignee_changed`   → invalidate detail (+ list once it renders assignee).
 * `ticket.tags_changed`       → invalidate list + detail so chips refresh on both.
 * `ticket.sla_warning`        → invalidate list + detail so the badge color flips.
 * `ticket.sla_breached`       → same.
 *
 * Mount this once below `<WebSocketProvider>` via the zero-render
 * `<QueryInvalidator>` component.
 */
function useQueryInvalidator() {
  const queryClient = useQueryClient();

  const onTicketCreated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
  }, [queryClient]);

  const onTicketTouched = useCallback(
    (payload: unknown) => {
      const evt = asEvent(payload);
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      if (evt.ticket_id) {
        queryClient.invalidateQueries({ queryKey: ["ticket", evt.ticket_id] });
      }
    },
    [queryClient],
  );

  useWebSocket("ticket.created", onTicketCreated);
  useWebSocket("ticket.message_added", onTicketTouched);
  useWebSocket("ticket.message_sent", onTicketTouched);
  useWebSocket("ticket.message_failed", onTicketTouched);
  useWebSocket("ticket.status_changed", onTicketTouched);
  useWebSocket("ticket.note_added", onTicketTouched);
  useWebSocket("ticket.note_deleted", onTicketTouched);
  useWebSocket("ticket.assignee_changed", onTicketTouched);
  useWebSocket("ticket.tags_changed", onTicketTouched);
  useWebSocket("ticket.sla_warning", onTicketTouched);
  useWebSocket("ticket.sla_breached", onTicketTouched);
}

export function QueryInvalidator() {
  useQueryInvalidator();
  return null;
}

/**
 * Hidden aria-live region. Subscribes to `ticket.created` and writes a short
 * announcement so screen readers pick it up without disrupting visible UI.
 * The `ticket.created` payload doesn't carry the contact name — the message
 * stays generic, and a user who wants details can navigate via the inbox or
 * the live-updating list.
 *
 * Two regions toggle in alternation. Many screen readers skip an aria-live
 * update when the textContent is identical to the previous one; alternating
 * which slot is non-empty guarantees a content change every time.
 */
export function NewTicketAnnouncer() {
  const [count, setCount] = useState(0);
  const onCreated = useCallback(() => setCount((n) => n + 1), []);
  useWebSocket("ticket.created", onCreated);
  const text = count > 0 ? "New ticket received." : "";
  const useFirstSlot = count % 2 === 1;
  return (
    <>
      <div role="status" aria-live="polite" className="sr-only">
        {useFirstSlot ? text : ""}
      </div>
      <div role="status" aria-live="polite" className="sr-only">
        {!useFirstSlot ? text : ""}
      </div>
    </>
  );
}
